import { createAsyncThunk } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import mixpanel from 'mixpanel-browser';
import FEATURES from 'src/featureMap';
import { ESFeedResponse } from 'src/types/dataTypes';
import { CodeusAPI } from 'src/networking/api';
import { DetectionFilter } from '../detectionSlice';

const AUTH_SESSION_KEY = 'authed';

function getAccountName(): string {
	try {
		const authed = sessionStorage.getItem(AUTH_SESSION_KEY);
		if (authed) {
			const parsed = JSON.parse(authed);
			return parsed?.accountDetails?.account?.name ?? '';
		}
	} catch {
		// ignore parse errors
	}
	return '';
}

function trackDetectionsFetch({
	accountId,
	searchText,
	searchField,
	filters,
	page,
	pageSize,
	sortBy,
	sortOrder,
	status,
	startDate,
	endDate,
	timeTaken,
	statusCode,
}: {
	accountId: string;
	searchText: string;
	searchField: string;
	filters: DetectionFilter[];
	page: number;
	pageSize: number;
	sortBy: string;
	sortOrder: string;
	status: string;
	startDate: string | null;
	endDate: string | null;
	timeTaken: number;
	statusCode: number;
}) {
	const action = FEATURES.detectionsFetch;
	const trackingData = {
		apiType: 'detections',
		params: [
			`page:${page}`,
			`pageSize:${pageSize}`,
			sortBy ? `sortBy:${sortBy}` : '',
			sortOrder ? `sortOrder:${sortOrder}` : '',
			status ? `status:${status}` : '',
			startDate ? `startDate:${startDate}` : '',
			endDate ? `endDate:${endDate}` : '',
			searchText ? `searchText:${searchText}` : '',
			searchField ? `searchField:${searchField}` : '',
			filters.length > 0
				? `filters:${filters
						.map((f) => `${f.fieldName}=${f.value.join(',')}`)
						.join(';')}`
				: '',
		]
			.filter(Boolean)
			.join('|'),
		timeTakenInMs: timeTaken,
		statusCode,
		accountId,
		accountName: getAccountName(),
	};
	mixpanel.track(`DI ${action.id}`, trackingData);
}

// Maps filter fieldName to the backend payload key
const FILTER_FIELD_MAP: Record<string, string> = {
	cloudType: 'source',
	sharingLevels: 'sharingLevel',
	tags: 'tags',
};

export const fetchAccountESFeeds = createAsyncThunk<
	ESFeedResponse,
	FetchESFeedsPayload,
	{ rejectValue: string }
>(
	'detections/fetchAccountESFeeds',
	async (
		{
			api,
			accountId,
			startDate,
			endDate,
			searchText = '',
			searchField = '',
			filters = [],
			page = 1,
			pageSize = 25,
			sortBy = 'lastUpdated',
			sortOrder = 'desc',
			status = 'Open',
		},
		{ rejectWithValue, signal },
	) => {
		const formattedStartDate = startDate
			? DateTime.fromISO(startDate)
					.toUTC()
					.toFormat('yyyy-MM-dd HH:mm:ss')
			: null;

		const formattedEndDate = endDate
			? DateTime.fromISO(endDate).toUTC().toFormat('yyyy-MM-dd HH:mm:ss')
			: null;

		// Build the request payload
		const reqPayload: Record<string, unknown> = {
			pageSize,
			pageNumber: page - 1,
			startTime: formattedStartDate || '',
			endTime: formattedEndDate || '',
			searchText,
			searchField,
			orderBy: sortBy === '' ? 'lastUpdated' : sortBy,
			sortOrder: sortOrder === 'desc' ? 'dsc' : sortOrder,
			status,
		};

		// Add filter properties to the payload
		filters.forEach((filter) => {
			const backendKey = FILTER_FIELD_MAP[filter.fieldName];
			if (backendKey) {
				reqPayload[backendKey] = filter.value;
				reqPayload[`${backendKey}Operation`] = filter.operation;
			}
		});

		const startTime = performance.now();

		try {
			const response = await api.getAccountESFeedsV2(
				accountId,
				reqPayload,
				{ signal },
			);

			if (!signal.aborted) {
				trackDetectionsFetch({
					accountId,
					searchText,
					searchField,
					filters,
					page,
					pageSize,
					sortBy,
					sortOrder,
					status,
					startDate: formattedStartDate,
					endDate: formattedEndDate,
					timeTaken: Math.round(performance.now() - startTime),
					statusCode: 200,
				});
			}

			return response;
		} catch (error) {
			if (signal.aborted) {
				return rejectWithValue('Request aborted');
			}

			const statusCode =
				error &&
				typeof error === 'object' &&
				'response' in error &&
				(error as any).response?.status
					? (error as any).response.status
					: 0;

			trackDetectionsFetch({
				accountId,
				searchText,
				searchField,
				filters,
				page,
				pageSize,
				sortBy,
				sortOrder,
				status,
				startDate: formattedStartDate,
				endDate: formattedEndDate,
				timeTaken: Math.round(performance.now() - startTime),
				statusCode,
			});

			console.error('Error fetching ES feeds:', error);
			const message =
				error instanceof Error
					? error.message
					: 'An unexpected error occurred';
			return rejectWithValue(message);
		}
	},
);

type FetchESFeedsPayload = {
	api: CodeusAPI;
	accountId: string;
	startDate: string;
	endDate: string;
	searchText?: string;
	searchField?: string;
	filters?: DetectionFilter[];
	page?: number;
	pageSize?: number;
	sortBy?: string;
	sortOrder?: string;
	status?: string;
};
